import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { motion } from 'framer-motion'
import { Heart, Bookmark, Share2, Send, ArrowLeft, Repeat, X, ChevronLeft, ChevronRight, Flag } from 'lucide-react'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { useAuthStore } from '@/stores/authStore'
import { getSocket } from '@/hooks/useSocket'
import api from '@/api/client'
import { toast } from '@/components/ui/toaster'
import { formatDistanceToNow, generateAnonymousAvatar } from '@/lib/utils'

export default function PostDetail() {
  const { t } = useTranslation()
  const { id } = useParams()
  const queryClient = useQueryClient()
  const { isAuthenticated } = useAuthStore()
  const [commentText, setCommentText] = useState('')
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [lightboxIndex, setLightboxIndex] = useState(0)
  const [reportReason, setReportReason] = useState('')
  const [showReportModal, setShowReportModal] = useState(false)

  const { data: post } = useQuery({
    queryKey: ['post', id],
    queryFn: () => api.get(`/posts/${id}`).then(r => r.data)
  })

  const { data: comments } = useQuery({
    queryKey: ['post-comments', id],
    queryFn: () => api.get(`/posts/${id}/comments`).then(r => r.data)
  })

  const { data: hopeChain } = useQuery({
    queryKey: ['post-hope-chain', id],
    queryFn: () => api.get(`/posts/${id}/hope-chain`).then(r => r.data),
    enabled: !!id,
  })

  const likeMutation = useMutation({
    mutationFn: () => api.post(`/posts/${id}/like`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['post', id] }),
    onError: (err: any) => toast({ title: err.response?.data?.error || t('common.error'), variant: 'destructive' })
  })

  const saveMutation = useMutation({
    mutationFn: () => api.post(`/posts/${id}/save`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['post', id] }),
    onError: (err: any) => toast({ title: err.response?.data?.error || t('common.error'), variant: 'destructive' })
  })

  const repostMutation = useMutation({
    mutationFn: () => api.post(`/posts/${id}/repost`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['post', id] })
      toast({ title: t('feed.reposted') })
    },
    onError: (err: any) => toast({ title: err.response?.data?.error || t('common.error'), variant: 'destructive' })
  })

  const reportMutation = useMutation({
    mutationFn: (reason: string) => api.post(`/posts/${id}/report`, { reason }),
    onSuccess: () => {
      setShowReportModal(false)
      setReportReason('')
      toast({ title: t('postDetail.reported') })
    },
    onError: (err: any) => toast({ title: err.response?.data?.error || t('common.error'), variant: 'destructive' })
  })

  // Real-time like and comment updates
  useEffect(() => {
    const socket = getSocket()
    if (!socket || !id) return
    const handleLike = (data: any) => {
      if (data.postId === id) {
        queryClient.setQueryData(['post', id], (old: any) => {
          if (!old) return old
          return { ...old, likes: data.likes }
        })
      }
    }
    const handleComment = (data: any) => {
      if (data.postId === id) {
        queryClient.invalidateQueries({ queryKey: ['post-comments', id] })
        queryClient.invalidateQueries({ queryKey: ['post', id] })
      }
    }
    socket.on('post:like', handleLike)
    socket.on('post:comment', handleComment)
    return () => {
      socket.off('post:like', handleLike)
      socket.off('post:comment', handleComment)
    }
  }, [id, queryClient])

  const commentMutation = useMutation({
    mutationFn: () => api.post(`/posts/${id}/comments`, { content: commentText }),
    onSuccess: () => {
      setCommentText('')
      queryClient.invalidateQueries({ queryKey: ['post-comments', id] })
      queryClient.invalidateQueries({ queryKey: ['post', id] })
      toast({ title: t('postDetail.commentPosted') })
    },
    onError: (err: any) => toast({ title: err.response?.data?.error || t('common.error'), variant: 'destructive' })
  })

  if (!post) {
    return <div className="max-w-2xl mx-auto px-4 py-12 text-center text-muted-foreground">{t('common.loading')}</div>
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-6">
      <Link to="/feed" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4 transition-colors">
        <ArrowLeft className="w-4 h-4" /> {t('postDetail.backToFeed')}
      </Link>

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="p-5 rounded-2xl bg-card border border-border mb-6">
        <div className="flex items-start gap-3">
          {post.isAnonymous ? (
            <Avatar className="w-10 h-10">
              <AvatarImage src={generateAnonymousAvatar(post._id).src} />
              <AvatarFallback className="text-white" style={{ backgroundColor: generateAnonymousAvatar(post._id).color }}>?</AvatarFallback>
            </Avatar>
          ) : (
            <Avatar className="w-10 h-10">
              <AvatarImage src={post.userId?.avatar} />
              <AvatarFallback>{post.username[0]?.toUpperCase()}</AvatarFallback>
            </Avatar>
          )}
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              {post.isAnonymous ? (
                <span className="font-semibold text-sm">{t('common.anonymous')}</span>
              ) : (
                <Link to={`/profile/${post.username}`} className="font-semibold text-sm hover:text-primary transition-colors">
                  {post.username}
                </Link>
              )}
              {!post.isAnonymous && post.userId?.expertType && (
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-primary/20 text-primary font-medium">
                  {t('expert.types.' + post.userId.expertType)}
                </span>
              )}
              <span className="text-muted-foreground text-xs">{formatDistanceToNow(post.createdAt)}</span>
            </div>
            <p className="text-sm leading-relaxed mb-3">{post.message}</p>
            {post.tags?.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-3">
                {post.tags.map((tag: string) => (
                  <Link key={tag} to={`/search?q=${encodeURIComponent(tag)}`}>
                    <span className="text-xs px-2 py-1 rounded-full bg-primary/10 text-primary hover:bg-primary/20 transition-colors">#{tag}</span>
                  </Link>
                ))}
              </div>
            )}
            {post.files?.length > 0 && (
              <div className="grid grid-cols-2 gap-2 mb-3">
                {post.files.map((file: string, i: number) => (
                  <img
                    key={i}
                    src={file}
                    alt=""
                    className="rounded-lg object-cover w-full h-48 cursor-pointer hover:opacity-90 transition-opacity"
                    onClick={() => { setLightboxIndex(i); setLightboxOpen(true); }}
                  />
                ))}
              </div>
            )}

            {/* Lightbox */}
            {lightboxOpen && post.files && (
              <div
                className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
                onClick={() => setLightboxOpen(false)}
              >
                <button
                  className="absolute top-4 right-4 p-2 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
                  onClick={() => setLightboxOpen(false)}
                >
                  <X className="w-6 h-6" />
                </button>
                {post.files.length > 1 && lightboxIndex > 0 && (
                  <button
                    className="absolute left-4 p-2 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
                    onClick={(e) => { e.stopPropagation(); setLightboxIndex(i => i - 1); }}
                  >
                    <ChevronLeft className="w-6 h-6" />
                  </button>
                )}
                <img
                  src={post.files[lightboxIndex]}
                  alt=""
                  className="max-w-full max-h-[90vh] object-contain rounded-lg"
                  onClick={(e) => e.stopPropagation()}
                />
                {post.files.length > 1 && lightboxIndex < post.files.length - 1 && (
                  <button
                    className="absolute right-4 p-2 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
                    onClick={(e) => { e.stopPropagation(); setLightboxIndex(i => i + 1); }}
                  >
                    <ChevronRight className="w-6 h-6" />
                  </button>
                )}
                {post.files.length > 1 && (
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1">
                    {post.files.map((_f: string, i: number) => (
                      <span key={i} className={`w-2 h-2 rounded-full ${i === lightboxIndex ? 'bg-white' : 'bg-white/40'}`} />
                    ))}
                  </div>
                )}
              </div>
            )}
            <div className="flex items-center gap-1">
              <button onClick={() => likeMutation.mutate()} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm ${post.liked ? 'text-red-500 bg-red-500/10' : 'text-muted-foreground hover:bg-secondary'}`}>
                <Heart className={`w-4 h-4 ${post.liked ? 'fill-current' : ''}`} /> {post.likes}
              </button>
              <button onClick={() => saveMutation.mutate()} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm ${post.saved ? 'text-yellow-500 bg-yellow-500/10' : 'text-muted-foreground hover:bg-secondary'}`}>
                <Bookmark className={`w-4 h-4 ${post.saved ? 'fill-current' : ''}`} />
              </button>
              <button
                onClick={() => repostMutation.mutate()}
                disabled={repostMutation.isPending}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm transition-colors ${post.reposted ? 'text-green-500 bg-green-500/10' : 'text-muted-foreground hover:bg-secondary'}`}
              >
                <Repeat className="w-4 h-4" />
              </button>
              <button onClick={() => { navigator.clipboard.writeText(window.location.href); toast({ title: t('common.copied') }) }} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm text-muted-foreground hover:bg-secondary">
                <Share2 className="w-4 h-4" />
              </button>
              <button onClick={() => setShowReportModal(true)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm text-muted-foreground hover:bg-secondary">
                <Flag className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Hope Chain */}
      {hopeChain && hopeChain.length > 0 && (
        <div className="mb-6">
          <h2 className="font-semibold mb-1">{t('postDetail.hopeChainTitle')}</h2>
          <p className="text-sm text-muted-foreground mb-3">{t('postDetail.hopeChainDescription')}</p>
          <div className="flex gap-3 overflow-x-auto pb-2">
            {hopeChain.map((chainPost: any) => (
              <motion.div
                key={chainPost._id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className={`min-w-[260px] p-4 rounded-xl border flex-shrink-0 ${chainPost._id === id ? 'border-primary bg-primary/5' : 'bg-card border-border'}`}
              >
                <div className="flex items-center gap-2 mb-2">
                  {chainPost.isAnonymous ? (
                    <Avatar className="w-8 h-8">
                      <AvatarImage src={generateAnonymousAvatar(chainPost._id).src} />
                      <AvatarFallback className="text-white" style={{ backgroundColor: generateAnonymousAvatar(chainPost._id).color }}>?</AvatarFallback>
                    </Avatar>
                  ) : (
                    <Avatar className="w-8 h-8">
                      <AvatarImage src={chainPost.userId?.avatar} />
                      <AvatarFallback>{chainPost.username[0]?.toUpperCase()}</AvatarFallback>
                    </Avatar>
                  )}
                  <div className="flex flex-col">
                    {chainPost.isAnonymous ? (
                      <span className="font-medium text-sm">{t('common.anonymous')}</span>
                    ) : (
                      <Link to={`/profile/${chainPost.username}`} className="font-medium text-sm hover:text-primary transition-colors">
                        {chainPost.username}
                      </Link>
                    )}
                    <span className="text-xs text-muted-foreground">{formatDistanceToNow(chainPost.createdAt)}</span>
                  </div>
                </div>
                <p className="text-sm line-clamp-3">{chainPost.message}</p>
              </motion.div>
            ))}
          </div>
        </div>
      )}
      {hopeChain?.length === 0 && (
        <div className="mb-6 text-sm text-muted-foreground">{t('postDetail.noHopeChain')}</div>
      )}

      {/* Comment Form */}
      {isAuthenticated && (
        <div className="mb-6 p-4 rounded-xl bg-card border border-border">
          <div className="flex items-start gap-3">
            <Avatar className="w-8 h-8">
              <AvatarFallback>?</AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <textarea
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                placeholder={t('postDetail.writeComment')}
                className="w-full px-3 py-2 rounded-lg bg-secondary border border-border focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none min-h-[60px] text-sm"
              />
              <div className="flex justify-end mt-2">
                <button
                  onClick={() => commentMutation.mutate()}
                  disabled={!commentText.trim() || commentMutation.isPending}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-50 transition-colors"
                >
                  <Send className="w-3.5 h-3.5" /> {t('common.send')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Comments */}
      <div className="space-y-3">
        <h2 className="font-semibold">{t('postDetail.comments')} ({comments?.length || 0})</h2>
        {comments?.map((c: any) => (
          <motion.div key={c._id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="p-4 rounded-xl bg-card border border-border">
            <div className="flex items-center gap-2 mb-2">
              <Avatar className="w-6 h-6">
                <AvatarImage src={c.avatar} />
                <AvatarFallback className="text-[10px]">{c.author?.[0]}</AvatarFallback>
              </Avatar>
              <Link to={`/profile/${c.author || ''}`} className="font-medium text-sm text-foreground hover:text-primary transition-colors">
                {c.author}
              </Link>
              <span className="text-xs text-muted-foreground">{formatDistanceToNow(c.createdAt)}</span>
            </div>
            <p className="text-sm pl-8">{c.content}</p>
          </motion.div>
        ))}
        {(!comments || comments.length === 0) && (
          <div className="text-center py-8 text-muted-foreground text-sm">{t('postDetail.noComments')}</div>
        )}
      </div>

      {/* Report Modal */}
      {showReportModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="w-full max-w-sm p-5 rounded-2xl bg-card border border-border shadow-lg">
            <h3 className="font-semibold mb-3">{t('postDetail.reportTitle')}</h3>
            <select
              value={reportReason}
              onChange={(e) => setReportReason(e.target.value)}
              className="w-full px-3 py-2 rounded-lg bg-secondary border border-border text-sm mb-3"
            >
              <option value="">{t('postDetail.reportReasonPlaceholder')}</option>
              <option value="inappropriate">{t('postDetail.reportReason.inappropriate')}</option>
              <option value="spam">{t('postDetail.reportReason.spam')}</option>
              <option value="hate">{t('postDetail.reportReason.hate')}</option>
              <option value="fraud">{t('postDetail.reportReason.fraud')}</option>
              <option value="other">{t('postDetail.reportReason.other')}</option>
            </select>
            <div className="flex gap-2">
              <button
                onClick={() => reportMutation.mutate(reportReason)}
                disabled={!reportReason || reportMutation.isPending}
                className="flex-1 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-50"
              >
                {t('postDetail.reportSubmit')}
              </button>
              <button
                onClick={() => setShowReportModal(false)}
                className="flex-1 py-2 rounded-lg bg-secondary text-sm font-medium hover:bg-secondary/80"
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
